import React, { useState, useEffect } from 'react';
import { Car, Truck, Bus, Bike, Ship, MapPin, CheckCircle, ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import { useSale } from '../context/SaleContext';
import { trackMetaEvent } from '../lib/meta';

// Fire a Meta Schedule event when a package is selected — non-invasive,
// wraps around existing selection logic. Contact fields will be empty at
// this stage (Calendly captures them later) but fbp/fbc/IP/UA still enrich
// match quality via CAPI.
const trackPackageSelected = (service, vehicle) => {
  trackMetaEvent({
    eventName: 'Schedule',
    customData: {
      value: service?.price,
      currency: 'CAD',
      content_type: 'product',
      content_name: service?.name,
      content_category: service?.category,
      content_ids: [`${vehicle?.id || 'unknown'}-${service?.id || 'unknown'}`],
    },
  });
};

const BookingWalkthrough = ({ isFallDeal = false, coatingFirst = false, isFriendsFamilyDiscount = false }) => {
  const { saleConfig, calculateSalePrice } = useSale();
  const [step, setStep] = useState(1);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCeramic, setSelectedCeramic] = useState(null);
  const [addInteriorBundle, setAddInteriorBundle] = useState(false);
  const [location, setLocation] = useState('');
  const [isValidLocation, setIsValidLocation] = useState(null);
  const [showCommercialForm, setShowCommercialForm] = useState(false);
  const [showCalendlyWidget, setShowCalendlyWidget] = useState(false);
  const [selectedCalendlyUrl, setSelectedCalendlyUrl] = useState('');
  const [showDetailServices, setShowDetailServices] = useState(false);

  // Helper to render price with sale or Friends & Family discount formatting
  // Friends & Family discount takes priority over regular sale
  const renderPrice = (price) => {
    if (isFriendsFamilyDiscount) {
      const discountedPrice = Math.round(price * 0.75);
      return (
        <div className="flex items-baseline gap-2">
          <span className="text-purple-400">${discountedPrice}</span>
          <span className="text-sm text-gray-500 line-through">${price}</span>
        </div>
      );
    }
    if (saleConfig.is_active) {
      const salePrice = calculateSalePrice(price);
      return (
        <div className="flex items-baseline gap-2">
          <span className="text-green-400">${salePrice}</span>
          <span className="text-sm text-gray-500 line-through">${price}</span>
        </div>
      );
    }
    return <span>${price}</span>;
  };

  // Get the discount badge to show - Friends & Family takes priority
  const getDiscountBadge = () => {
    if (isFriendsFamilyDiscount) {
      return (
        <div className="absolute -top-3 right-6 bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
          25% OFF
        </div>
      );
    }
    if (saleConfig.is_active) {
      return (
        <div className="absolute -top-3 right-6 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
          20% OFF
        </div>
      );
    }
    return null;
  };

  // Service areas for validation
  const serviceAreas = [
    'abbotsford', 'central abbotsford', 'clearbrook', 'matsqui', 'sumas', 'bradner', 'mount lehman', 'hatzic',
    'surrey', 'cloverdale', 'fleetwood', 'guildford', 'newton', 'whalley', 'south surrey', 'ocean park', 'crescent beach',
    'white rock', 'central white rock', 'east beach', 'west beach',
    'langley', 'aldergrove', 'brookswood', 'fort langley', 'murrayville', 'walnut grove', 'willoughby',
    'mission', 'downtown mission', 'silverdale', 'hatzic', 'stave falls',
    'maple ridge', 'albion', 'haney', 'silver valley', 'whonnock',
    'pitt meadows', 'central pitt meadows', 'north pitt meadows',
    'clayburn', 'ruskin', 'thornhill'
  ];

  const vehicles = [
    { id: 'car', name: 'Car', icon: Car, description: 'Sedans, hatchbacks, coupes' },
    { id: 'suv', name: 'SUV', icon: Car, description: 'SUVs, crossovers' },
    { id: 'truck', name: 'Truck', icon: Truck, description: 'Standard pickup trucks' },
    { id: 'boat', name: 'Boat', icon: Ship, description: 'Boats, watercraft' },
    { id: 'lifted', name: 'Lifted Truck/Dually', icon: Truck, description: 'Large trucks & duallies' },
    { id: 'motorcycle', name: 'Motorcycle', icon: Bike, description: 'Bikes & motorcycles' }
  ];

  const getServiceOptions = (vehicleType) => {
    const isLifted = vehicleType === 'lifted';
    
    // Regular detailing services
    const detailingServices = (() => {
      switch (vehicleType) {
        case 'motorcycle':
          return [
            {
              id: 'motorcycle-detail',
              name: 'Motorcycle Detail',
              price: 199,
              description: 'Complete motorcycle detailing with ceramic wax coating, wheel cleaning, chrome polishing, and leather conditioning',
              category: 'detailing'
            }
          ];
        case 'lifted':
          return [
            {
              id: 'full-detail-lifted',
              name: 'Full Detail Package',
              price: 348,
              description: 'Interior + Exterior with 6-12 month ceramic wax coating',
              category: 'detailing'
            },
            {
              id: 'interior-lifted',
              name: 'Interior Only',
              price: 199,
              description: 'Deep interior cleaning and conditioning',
              category: 'detailing'
            },
            {
              id: 'exterior-lifted',
              name: 'Exterior Only', 
              price: 149,
              description: 'Complete exterior wash and ceramic wax coating',
              category: 'detailing'
            }
          ];
        default:
          return [
            {
              id: 'full-detail',
              name: 'Full Detail Package',
              price: 299,
              description: 'Interior + Exterior with 6-12 month ceramic wax coating',
              popular: true,
              category: 'detailing'
            },
            {
              id: 'interior',
              name: 'Interior Only',
              price: 199,
              description: 'Deep interior cleaning and conditioning',
              category: 'detailing'
            },
            {
              id: 'exterior',
              name: 'Exterior Only',
              price: 149,
              description: 'Complete exterior wash and ceramic wax coating',
              category: 'detailing'
            }
          ];
      }
    })();

    // Add ceramic coating options for non-motorcycle vehicles
    if (vehicleType !== 'motorcycle') {
      const ceramicOptions = [
        {
          id: 'ceramic-8year',
          name: '8-Year Signature Ceramic Coating',
          brand: 'Crystal Brite + Single Stage Polish',
          price: isLifted ? 848 : 799,
          duration: '6-8 years',
          description: 'Crystal Brite Semi-Permanent Coating with 9H hardness — insane hydrophobics, self-cleaning, high-definition shine',
          category: 'ceramic',
          popular: true,
          interiorBundleAvailable: true
        }
      ];
      
      return [...detailingServices, ...ceramicOptions];
    }
    
    return detailingServices;
  };

  const getCalendlyLink = (vehicleType, serviceId, withInteriorBundle = false) => {
    const calendlyParams = '?hide_event_type_details=1&hide_gdpr_banner=1&background_color=302F2C&text_color=CCCCCC&primary_color=0069ff';
    
    const calendlyLinks = {
      // Car
      'car-full-detail': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-2' + calendlyParams,
      'car-interior': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-3' + calendlyParams,
      'car-exterior': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-4' + calendlyParams,
      
      // SUV
      'suv-full-detail': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-clone' + calendlyParams,
      'suv-interior': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-clone-1' + calendlyParams,
      'suv-exterior': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-clone-2' + calendlyParams,
      
      // Truck
      'truck-full-detail': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-clone-3' + calendlyParams,
      'truck-interior': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-clone-4' + calendlyParams,
      'truck-exterior': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-clone-5' + calendlyParams,
      
      // Motorcycle
      'motorcycle-motorcycle-detail': 'https://calendly.com/prowashmd-info/truck-exterior-only-clone' + calendlyParams,
      
      // Lifted Truck/Dually - Detailing
      'lifted-full-detail-lifted': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone-1' + calendlyParams,
      'lifted-interior-lifted': 'https://calendly.com/prowashmd-info/book-your-luxury-detailing-appointment-clone' + calendlyParams,
      'lifted-exterior-lifted': 'https://calendly.com/prowashmd-info/dually-lifted-truck-interior-only-clone' + calendlyParams,
      
      // Ceramic Coatings - Car/SUV/Truck (8-Year)
      'car-ceramic-8year': 'https://calendly.com/prowashmd-info/5-year_ceramic_coating' + calendlyParams,
      'car-ceramic-8year-bundle': 'https://calendly.com/prowashmd-info/5-year-ceramic-coating-clone' + calendlyParams,
      'suv-ceramic-8year': 'https://calendly.com/prowashmd-info/5-year_ceramic_coating' + calendlyParams,
      'suv-ceramic-8year-bundle': 'https://calendly.com/prowashmd-info/5-year-ceramic-coating-clone' + calendlyParams,
      'truck-ceramic-8year': 'https://calendly.com/prowashmd-info/5-year_ceramic_coating' + calendlyParams,
      'truck-ceramic-8year-bundle': 'https://calendly.com/prowashmd-info/5-year-ceramic-coating-clone' + calendlyParams,
      
      // Ceramic Coatings - Lifted/Dually (8-Year)
      'lifted-ceramic-8year': 'https://calendly.com/prowashmd-info/5-year-ceramic-coating-clone-1' + calendlyParams,
      'lifted-ceramic-8year-bundle': 'https://calendly.com/prowashmd-info/5-year-ceramic-coating-interior-bundle-clone' + calendlyParams
    };

    let key = `${vehicleType}-${serviceId}`;
    if (withInteriorBundle && serviceId === 'ceramic-8year') {
      key = `${vehicleType}-${serviceId}-bundle`;
    }
    return calendlyLinks[key] || null;
  };

  const handleBookingSubmit = (vehicleType, serviceId, withInteriorBundle = false) => {
    if (vehicleType === 'boat') {
      // Show contact form for boats
      setShowCommercialForm(true);
      return;
    }

    console.log('Booking Submit:', { vehicleType, serviceId, withInteriorBundle });
    const calendlyLink = getCalendlyLink(vehicleType, serviceId, withInteriorBundle);
    console.log('Calendly Link:', calendlyLink);
    
    if (calendlyLink) {
      // Show embedded Calendly widget
      setShowCalendlyWidget(true);
      setSelectedCalendlyUrl(calendlyLink);
    } else {
      alert('Booking link not available. Please call (604) 613-8901 to book.');
    }
  };

  const validateLocation = (locationInput) => {
    const cleanLocation = locationInput.toLowerCase().trim();
    return serviceAreas.some(area => 
      cleanLocation.includes(area) || area.includes(cleanLocation)
    );
  };

  const handleLocationSubmit = () => {
    const isValid = validateLocation(location);
    setIsValidLocation(isValid);
    if (isValid) {
      // In real app, this would open Calendly
      alert('Great! Calendly booking system would open here with available 8AM and 1PM slots.');
    }
  };

  const resetWalkthrough = () => {
    setStep(1);
    setSelectedVehicle(null);
    setSelectedService(null);
    setLocation('');
    setIsValidLocation(null);
    setShowCommercialForm(false);
    setShowCalendlyWidget(false);
    setSelectedCalendlyUrl('');
  };

  // Initialize Calendly widget when shown
  useEffect(() => {
    if (showCalendlyWidget && selectedCalendlyUrl) {
      // Wait for Calendly script to load if not already loaded
      const initWidget = () => {
        const widget = document.querySelector('.calendly-inline-widget');
        if (widget && window.Calendly) {
          // Clear existing content
          widget.innerHTML = '';
          
          // Initialize the Calendly widget
          window.Calendly.initInlineWidget({
            url: selectedCalendlyUrl,
            parentElement: widget,
            prefill: {},
            utm: {}
          });
        }
      };

      // If Calendly is already loaded, initialize immediately
      if (window.Calendly) {
        initWidget();
      } else {
        // Wait for Calendly to load
        const checkCalendly = setInterval(() => {
          if (window.Calendly) {
            clearInterval(checkCalendly);
            initWidget();
          }
        }, 100);
        
        // Clean up interval after 10 seconds
        setTimeout(() => clearInterval(checkCalendly), 10000);
      }
    }
  }, [showCalendlyWidget, selectedCalendlyUrl]);

  // Redirect to /thank-you once Calendly confirms the booking is actually
  // created. Gated on payload.event.uri AND payload.invitee.uri to avoid
  // firing during Calendly's phone-verification step (those URIs only
  // exist AFTER verification succeeds).
  useEffect(() => {
    const handleCalendlyEvent = (e) => {
      if (!e.data || e.data.event !== 'calendly.event_scheduled') return;
      const payload = e.data.payload || {};
      const hasEventUri = !!(payload.event && payload.event.uri);
      const hasInviteeUri = !!(payload.invitee && payload.invitee.uri);
      if (!hasEventUri || !hasInviteeUri) return;
      setTimeout(() => {
        window.location.href = '/thank-you';
      }, 2500);
    };
    window.addEventListener('message', handleCalendlyEvent);
    return () => window.removeEventListener('message', handleCalendlyEvent);
  }, []);

  return (
    <section id="booking-walkthrough" className="pad-120 bg-black">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="heading-1 mb-6">Book Your Premium Detail</h2>
          <p className="body-large text-gray-400 max-w-3xl mx-auto">
            Get that "new car feel" with our mobile detailing service. Complete your booking in 3 easy steps.
          </p>
          
          {/* Progress Indicator */}
          <div className="flex justify-center items-center mt-8 space-x-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
            }`}>1</div>
            <div className={`w-16 h-1 ${step >= 2 ? 'bg-blue-500' : 'bg-gray-700'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
            }`}>2</div>
            <div className={`w-16 h-1 ${step >= 3 ? 'bg-blue-500' : 'bg-gray-700'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
            }`}>3</div>
          </div>
        </div>

        {/* Step 1: Vehicle Selection */}
        {step === 1 && (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="heading-2 mb-4">Select Your Vehicle Type</h3>
              <p className="body-medium text-gray-400">Choose the vehicle you'd like us to detail</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((vehicle) => {
                const IconComponent = vehicle.icon;
                return (
                  <button
                    key={vehicle.id}
                    onClick={() => {
                      setSelectedVehicle(vehicle);
                      if (vehicle.id === 'boat') {
                        setStep(3);
                        setShowCommercialForm(true);
                      } else {
                        setStep(2);
                      }
                    }}
                    className="service-card text-center hover:ring-2 hover:ring-blue-500 transition-all group"
                  >
                    <div className="mb-4">
                      <IconComponent size={48} className="text-blue-400 mx-auto group-hover:text-white transition-colors" />
                    </div>
                    <h4 className="heading-3 text-lg mb-2">{vehicle.name}</h4>
                    <p className="body-small text-gray-400">{vehicle.description}</p>
                  </button>
                );
              })}
            </div>
            
            <div className="text-center mt-12">
              <p className="body-small text-gray-500">
                Note: We do not service semi-vehicles. Contact us for other vehicle types.
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Service Selection */}
        {step === 2 && selectedVehicle && (
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="heading-2 mb-4">{coatingFirst ? 'Choose Your Coating' : 'Choose Your Service'}</h3>
              <p className="body-medium text-gray-400">
                Selected: <span className="text-white font-semibold">{selectedVehicle.name}</span>
              </p>
            </div>

            {/* Ceramic Coating Services Section - Show FIRST when coatingFirst is true */}
            {coatingFirst && selectedVehicle.id !== 'motorcycle' && (
              <div className="mb-12">
                <div className="text-center mb-6">
                  <h4 className="heading-3 text-xl text-green-400">Premium Ceramic Coating</h4>
                  <p className="text-sm text-gray-400 mt-2">Professional 9H paint protection • Incredible Longevity</p>
                </div>
                {getServiceOptions(selectedVehicle.id).filter(s => s.category === 'ceramic').map((service) => (
                  <div
                    key={service.id}
                    onClick={() => {
                      setSelectedService(service);
                      setSelectedCeramic(service);
                      if (service.interiorBundleAvailable) {
                        setStep(2.5);
                        setTimeout(() => {
                          const element = document.querySelector('#booking-walkthrough');
                          if (element) {
                            element.scrollIntoView({ behavior: 'instant', block: 'start' });
                          }
                        }, 10);
                      } else {
                        setStep(3);
                      }
                      
                      if (typeof window !== 'undefined' && window.fbq) {
                        window.fbq('track', 'AddToCart', {
                          value: service.price,
                          currency: 'CAD',
                          content_type: 'product',
                          content_ids: [`${selectedVehicle.id}-${service.id}`],
                          content_name: `${selectedVehicle.name} - ${service.name}`,
                          content_category: 'Ceramic Coating'
                        });
                      }
                      trackPackageSelected(service, selectedVehicle);
                    }}
                    className="service-card border-2 border-green-500/30 hover:border-green-500 transition-all cursor-pointer relative"
                  >
                    {(isFriendsFamilyDiscount || saleConfig.is_active) && (
                      <div className={`absolute -top-3 right-6 ${isFriendsFamilyDiscount ? 'bg-purple-500' : 'bg-green-500'} text-white px-3 py-1 rounded-full text-xs font-bold uppercase`}>
                        {isFriendsFamilyDiscount ? '25% OFF' : '20% OFF'}
                      </div>
                    )}
                    
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="heading-3 text-xl text-white mb-1">{service.name}</h4>
                        <p className="text-sm text-green-400 mb-3">{service.brand}</p>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-300">
                          <span>✓ High-Definition Depth & Shine</span>
                          <span>✓ 9H Ceramic Hardness</span>
                          <span>✓ Self-cleaning Hydrophobics</span>
                        </div>
                      </div>
                      <div className="text-left md:text-right">
                        <div className="text-3xl font-bold text-green-400">{renderPrice(service.price)}</div>
                        <div className="text-sm text-gray-500">{service.duration} protection</div>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-700/50">
                      <p className="text-xs text-blue-300">For optimal results, a garage or covered workspace is required for all Ceramic Coating installations.</p>
                    </div>
                  </div>
                ))}
                
                {/* Clickable expander for detail services */}
                <div className="relative my-10">
                  <button 
                    onClick={() => setShowDetailServices(!showDetailServices)}
                    className="w-full flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-blue-400 transition-colors py-2"
                  >
                    <span>or choose a detail service</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showDetailServices ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              </div>
            )}

            {/* Detailing Services Section - Hidden by default in coatingFirst mode */}
            {(!coatingFirst || showDetailServices) && (
            <div className="mb-12">
              <h4 className="heading-3 text-xl mb-6 text-center text-blue-400">Mobile Detailing Services</h4>
              <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
                {getServiceOptions(selectedVehicle.id).filter(s => s.category === 'detailing').map((service) => (
                  <button
                    key={service.id}
                    onClick={() => {
                      setSelectedService(service);
                      setSelectedCeramic(null);
                      setAddInteriorBundle(false);
                      setStep(3);
                      
                      // Track AddToCart event when service is selected
                      if (typeof window !== 'undefined' && window.fbq) {
                        let contentId = `${selectedVehicle.id}-${service.id}`;
                        let trackingValue = service.price;
                        if (selectedVehicle.id === 'lifted') {
                          trackingValue = 299;
                        }
                        
                        window.fbq('track', 'AddToCart', {
                          value: trackingValue,
                          currency: 'CAD',
                          content_type: 'product',
                          content_ids: [contentId],
                          content_name: `${selectedVehicle.name} - ${service.name}`,
                          content_category: 'Mobile Car Detailing'
                        });
                      }
                      trackPackageSelected(service, selectedVehicle);
                    }}
                    className={`service-card text-left hover:ring-2 hover:ring-blue-500 transition-all relative ${
                      service.popular ? 'ring-2 ring-blue-400' : ''
                    }`}
                  >
                    {service.popular && (
                      <div className="absolute -top-4 left-6 bg-blue-400 text-black px-4 py-1 rounded-full text-sm font-semibold uppercase">
                        Most Popular
                      </div>
                    )}
                    
                    {(isFriendsFamilyDiscount || saleConfig.is_active) && (
                      <div className={`absolute -top-4 right-6 ${isFriendsFamilyDiscount ? 'bg-purple-500' : 'bg-green-500'} text-white px-3 py-1 rounded-full text-xs font-bold uppercase`}>
                        {isFriendsFamilyDiscount ? '25% OFF' : '20% OFF'}
                      </div>
                    )}
                    
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="heading-3 text-lg">{service.name}</h4>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-400">{renderPrice(service.price)}</div>
                      </div>
                    </div>
                    
                    <p className="body-small text-gray-400">{service.description}</p>
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Ceramic Coating Services Section - Show AFTER detailing when NOT coatingFirst */}
            {!coatingFirst && selectedVehicle.id !== 'motorcycle' && (
              <div className="mb-8">
                <div className="text-center mb-6">
                  <h4 className="heading-3 text-xl text-green-400">Premium Ceramic Coating</h4>
                </div>
                {getServiceOptions(selectedVehicle.id).filter(s => s.category === 'ceramic').map((service) => (
                  <div
                    key={service.id}
                    onClick={() => {
                      setSelectedService(service);
                      setSelectedCeramic(service);
                      if (service.interiorBundleAvailable) {
                        setStep(2.5);
                        setTimeout(() => {
                          const element = document.querySelector('#booking-walkthrough');
                          if (element) {
                            element.scrollIntoView({ behavior: 'instant', block: 'start' });
                          }
                        }, 10);
                      } else {
                        setStep(3);
                      }
                      
                      if (typeof window !== 'undefined' && window.fbq) {
                        window.fbq('track', 'AddToCart', {
                          value: service.price,
                          currency: 'CAD',
                          content_type: 'product',
                          content_ids: [`${selectedVehicle.id}-${service.id}`],
                          content_name: `${selectedVehicle.name} - ${service.name}`,
                          content_category: 'Ceramic Coating'
                        });
                      }
                      trackPackageSelected(service, selectedVehicle);
                    }}
                    className="service-card border-2 border-green-500/30 hover:border-green-500 transition-all cursor-pointer relative"
                  >
                    {(isFriendsFamilyDiscount || saleConfig.is_active) && (
                      <div className={`absolute -top-3 right-6 ${isFriendsFamilyDiscount ? 'bg-purple-500' : 'bg-green-500'} text-white px-3 py-1 rounded-full text-xs font-bold uppercase`}>
                        {isFriendsFamilyDiscount ? '25% OFF' : '20% OFF'}
                      </div>
                    )}
                    
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="heading-3 text-xl text-white mb-1">{service.name}</h4>
                        <p className="text-sm text-green-400 mb-3">{service.brand}</p>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-300">
                          <span>✓ High-Definition Depth & Shine</span>
                          <span>✓ 9H Ceramic Hardness</span>
                          <span>✓ Self-cleaning Hydrophobics</span>
                        </div>
                      </div>
                      <div className="text-left md:text-right">
                        <div className="text-3xl font-bold text-green-400">{renderPrice(service.price)}</div>
                        <div className="text-sm text-gray-500">{service.duration} protection</div>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-700/50">
                      <p className="text-xs text-blue-300">For optimal results, a garage or covered workspace is required for all Ceramic Coating installations.</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add-Ons Section - Hide for coating-first flow */}
            {!coatingFirst && (
            <div className="service-card border border-gray-700/50">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <h4 className="heading-3 text-lg text-white">Available Add-Ons</h4>
                <span className="text-sm text-gray-400">$49 each • Select during booking</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-gray-800/50 rounded-full text-sm text-gray-300 border border-gray-700/50">Engine Bay Cleaning</span>
                <span className="px-4 py-2 bg-gray-800/50 rounded-full text-sm text-gray-300 border border-gray-700/50">Pet Hair Removal</span>
                <span className="px-4 py-2 bg-gray-800/50 rounded-full text-sm text-gray-300 border border-gray-700/50">Heavy Stain/Odor Removal</span>
                <span className="px-4 py-2 bg-gray-800/50 rounded-full text-sm text-gray-300 border border-gray-700/50">Headlight Restoration</span>
              </div>
            </div>
            )}

            <div className="text-center mt-8">
              <button
                onClick={() => setStep(1)}
                className="btn-secondary mr-4"
              >
                <ArrowLeft size={16} />
                <span className="ml-2">Back</span>
              </button>
            </div>
          </div>
        )}

        {/* Step 2.5: Interior Bundle Selection for Ceramic Coatings */}
        {step === 2.5 && selectedCeramic && (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="heading-2 mb-4">Add Interior Bundle?</h3>
              <p className="body-medium text-gray-400">
                Selected: <span className="text-white font-semibold">{selectedVehicle.name} - {selectedCeramic.name}</span>
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Ceramic Only */}
              <button
                onClick={() => {
                  setAddInteriorBundle(false);
                  setStep(3);
                }}
                className="service-card text-left hover:ring-2 hover:ring-green-500 transition-all relative"
              >
                {(isFriendsFamilyDiscount || saleConfig.is_active) && (
                  <div className={`absolute -top-3 right-6 ${isFriendsFamilyDiscount ? 'bg-purple-500' : 'bg-green-500'} text-white px-3 py-1 rounded-full text-xs font-bold uppercase`}>
                    {isFriendsFamilyDiscount ? '25% OFF' : '20% OFF'}
                  </div>
                )}
                <div className="flex justify-between items-start mb-4">
                  <h4 className="heading-3 text-lg">Ceramic Coating Only</h4>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-green-400">{renderPrice(selectedCeramic.price)}</div>
                    <div className="text-xs text-gray-500">Exterior only</div>
                  </div>
                </div>
                <p className="body-small text-gray-400">Professional ceramic coating without interior service</p>
              </button>

              {/* Ceramic + Interior Bundle */}
              <button
                onClick={() => {
                  setAddInteriorBundle(true);
                  setStep(3);
                }}
                className="service-card text-left hover:ring-2 hover:ring-blue-500 transition-all border-2 border-blue-500/50 relative"
              >
                {(isFriendsFamilyDiscount || saleConfig.is_active) && (
                  <div className={`absolute -top-3 right-6 ${isFriendsFamilyDiscount ? 'bg-purple-500' : 'bg-green-500'} text-white px-3 py-1 rounded-full text-xs font-bold uppercase`}>
                    {isFriendsFamilyDiscount ? '25% OFF' : '20% OFF'}
                  </div>
                )}
                <div className="flex justify-between items-start mb-4">
                  <h4 className="heading-3 text-lg">Ceramic + Interior</h4>
                  <div className="text-right">
                    {saleConfig.is_active ? (
                      <>
                        <div className="text-sm text-gray-500 line-through">${selectedCeramic.price + 199}</div>
                        <div className="text-3xl font-bold text-green-400">${calculateSalePrice(selectedCeramic.price + 199)}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-gray-500">${selectedCeramic.price} + $199</div>
                        <div className="text-3xl font-bold text-blue-400">${selectedCeramic.price + 199}</div>
                      </>
                    )}
                    <div className="text-xs text-gray-500">Complete package</div>
                  </div>
                </div>
                <p className="body-small text-gray-400">Ceramic coating + full interior deep clean & conditioning</p>
              </button>
            </div>

            <div className="text-center mt-8">
              <button
                onClick={() => setStep(2)}
                className="btn-secondary"
              >
                <ArrowLeft size={16} />
                <span className="ml-2">Back to Services</span>
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Booking - External Calendly Links */}
        {step === 3 && (selectedService || selectedVehicle.id === 'boat') && (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="heading-2 mb-4">Complete Your Booking</h3>
              <p className="body-medium text-gray-400">
                Selected: <span className="text-white font-semibold">
                  {selectedVehicle.id === 'boat' 
                    ? selectedVehicle.name 
                    : `${selectedVehicle.name} - ${selectedService.name}${addInteriorBundle ? ' + Interior Bundle' : ''}`}
                </span>
              </p>
              {addInteriorBundle && (
                <p className="body-medium text-blue-400 mt-2">
                  Total: <span className="font-bold text-2xl">
                    {saleConfig.is_active ? (
                      <>
                        <span className="text-green-400">${calculateSalePrice(selectedService.price + 199)}</span>
                        <span className="text-sm text-gray-500 line-through ml-2">${selectedService.price + 199}</span>
                      </>
                    ) : (
                      `$${selectedService.price + 199}`
                    )}
                  </span>
                </p>
              )}
            </div>

            {selectedVehicle.id === 'boat' ? (
              /* Boat Quote Form */
              <div className="service-card">
                <h4 className="heading-3 mb-6 text-center">Boat Quote</h4>
                <div className="text-center">
                  <p className="body-medium text-gray-300 mb-6">
                    Boats require custom quotes based on size and requirements.
                  </p>
                  <div className="space-y-4">
                    <button 
                      onClick={() => {
                        window.location.href = `tel:(604) 613-8901`;
                      }}
                      className="btn-primary w-full"
                    >
                      Call (604) 613-8901
                    </button>
                    <button 
                      onClick={() => window.location.href = `mailto:info@prowashmd.com`}
                      className="btn-secondary w-full"
                    >
                      Email Us
                    </button>
                  </div>
                </div>
              </div>
            ) : showCalendlyWidget ? (
              /* Embedded Calendly Widget */
              <div className="service-card">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="heading-3">Book Your Appointment</h4>
                  <button 
                    onClick={() => setShowCalendlyWidget(false)}
                    className="btn-secondary px-4 py-2 text-sm"
                  >
                    ← Back
                  </button>
                </div>
                <div className="text-center mb-6">
                  <p className="body-medium text-gray-300">
                    Selected: <span className="text-white font-semibold">{selectedVehicle.name} - {selectedService.name}{addInteriorBundle ? ' + Interior Bundle' : ''}</span>
                  </p>
                </div>
                
                {/* Calendly Inline Widget */}
                <div 
                  className="calendly-inline-widget" 
                  data-url={selectedCalendlyUrl}
                  style={{
                    minWidth: '320px',
                    height: '700px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#302f2c'
                  }}
                >
                  {/* Loading indicator while Calendly loads */}
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-400">Loading calendar...</div>
                  </div>
                </div>
                
                <div className="text-center mt-6">
                  <p className="body-small text-gray-400">
                    Available time slots: {selectedService?.category === 'ceramic' ? '8AM start' : '8AM or 1PM'} • No deposit required
                  </p>
                </div>
              </div>
            ) : (
              /* Booking Button - Shows Calendly Widget */
              <div className="service-card">
                <h4 className="heading-3 mb-6 text-center">Book Your Appointment</h4>
                <div className="text-center">
                  <button 
                    onClick={() => handleBookingSubmit(selectedVehicle.id, selectedService.id, addInteriorBundle)}
                    className="btn-primary w-full flex items-center justify-center space-x-2"
                  >
                    <span>Book Now - {selectedService?.category === 'ceramic' ? '8AM Start' : 'Available 8AM or 1PM'}</span>
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}

            <div className="text-center mt-8">
              {selectedVehicle.id !== 'boat' && (
                <button
                  onClick={() => setStep(2)}
                  className="btn-secondary mr-4"
                >
                  <ArrowLeft size={16} />
                  <span className="ml-2">Back</span>
                </button>
              )}
              <button
                onClick={resetWalkthrough}
                className="btn-secondary"
              >
                Start Over
              </button>
            </div>
          </div>
        )}

        {/* Service Areas Info */}
        <div className="mt-16 pt-16 border-t border-gray-800">
          <div className="text-center">
            <h4 className="heading-3 mb-6">We Serve the Lower Mainland</h4>
            <p className="body-medium text-gray-400">Abbotsford, Langley, Mission & Maple Ridge and surrounding areas</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingWalkthrough;